import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Mail, Phone, QrCode, X, ArrowUpRight, ChevronDown, ArrowUp, MessageSquare } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';

const SideButton = ({ theme = 'light' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showWeChatQR, setShowWeChatQR] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const isDark = theme === 'dark';

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check, { passive: true });
    return () => window.removeEventListener('resize', check);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const toggleMenu = () => {
    setIsOpen(prev => { const next = !prev; if (!next) setShowWeChatQR(false); return next; });
  };
  const handleWeChatClick = (e) => { e.preventDefault(); setShowWeChatQR(prev => !prev); };

  const panelBg = isDark ? 'rgba(10,10,10,0.97)' : 'rgba(255,255,255,0.98)';
  const panelBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)';
  const rowBorder = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)';
  const rowHover = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
  const textMain = isDark ? '#f9fafb' : '#111827';
  const textSub = isDark ? '#6b7280' : '#9ca3af';
  const chipBg = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)';

  const contactLinks = [
    { icon: <MessageCircle size={18} />, label: 'WhatsApp', sub: '+86 139 1322 5243', href: 'https://wa.me/8613913225243', color: '#22c55e' },
    { icon: <Mail size={18} />, label: 'Email', sub: 'tony@qsconn.com', href: 'mailto:tony@qsconn.com', color: '#3b82f6' },
    { icon: <Phone size={18} />, label: 'Call', sub: '+86 180 1328 9913', href: 'tel:+8618013289913', color: '#dc2626' },
  ];

  // ── MOBILE: Sticky bottom bar ──
  if (isMobile) {
    return (
      <>
        {/* Sticky Contact Bar */}
        <div style={{
          position: 'fixed',
          bottom: 0, left: 0, right: 0,
          zIndex: 900,
          padding: '10px 12px',
          background: isDark
            ? 'linear-gradient(to top, rgba(0,0,0,0.98), rgba(0,0,0,0.92))'
            : 'linear-gradient(to top, rgba(255,255,255,0.98), rgba(255,255,255,0.92))',
          borderTop: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.08)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          display: 'flex',
          gap: 8,
        }}>
          {/* WhatsApp quick button */}
          <a
            href="https://wa.me/8613913225243"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 42, height: 42, borderRadius: 12, flexShrink: 0,
              background: 'rgba(34,197,94,0.12)',
              border: '1.5px solid rgba(34,197,94,0.25)',
              color: '#22c55e',
              textDecoration: 'none',
              minHeight: 'unset', minWidth: 'unset',
            }}
            aria-label="WhatsApp"
          >
            <MessageCircle size={18} />
          </a>

          {/* Phone quick button */}
          <a
            href="tel:+8618013289913"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 42, height: 42, borderRadius: 12, flexShrink: 0,
              background: 'rgba(220,38,38,0.1)',
              border: '1.5px solid rgba(220,38,38,0.25)',
              color: '#dc2626',
              textDecoration: 'none',
              minHeight: 'unset', minWidth: 'unset',
            }}
            aria-label="Call us"
          >
            <Phone size={18} />
          </a>

          {/* Main CTA */}
          <Link
            to="/contact"
            style={{
              flex: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              height: 42, borderRadius: 12,
              background: 'linear-gradient(135deg, #dc2626, #991b1b)',
              color: '#fff', fontWeight: 700, fontSize: 14,
              textDecoration: 'none',
              boxShadow: '0 4px 16px rgba(220,38,38,0.35)',
              minHeight: 'unset', minWidth: 'unset',
            }}
          >
            <MessageSquare size={15} />
            Get a Free Quote
          </Link>
        </div>

        {/* Scroll to top (above sticky bar) */}
        {showScrollTop && (
          <button
            type="button"
            onClick={scrollToTop}
            aria-label="Scroll to top"
            style={{
              position: 'fixed',
              bottom: 72, right: 14,
              zIndex: 901,
              width: 38, height: 38, borderRadius: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: isDark ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.95)',
              border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
              color: isDark ? '#9ca3af' : '#6b7280',
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              cursor: 'pointer',
              backdropFilter: 'blur(10px)',
              minHeight: 'unset', minWidth: 'unset',
            }}
          >
            <ArrowUp size={15} />
          </button>
        )}
      </>
    );
  }

  // ── DESKTOP: Original vertical side button ──
  return (
    <div style={{
      position: 'fixed',
      right: 20, top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 500,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
    }}>
      {/* Contact panel */}
      {isOpen && (
        <div style={{
          width: 270, borderRadius: 20,
          border: `1px solid ${panelBorder}`,
          background: panelBg,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.6)' : '0 20px 60px rgba(0,0,0,0.12)',
          overflow: 'hidden',
          animation: 'sideIn 0.2s ease',
        }}>
          {/* Header */}
          <div style={{
            padding: '16px 18px',
            borderBottom: `1px solid ${rowBorder}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.5, color: textSub, textTransform: 'uppercase', marginBottom: 2 }}>
                Contact Us
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: textMain }}>We're ready to help</div>
            </div>
            <button
              onClick={toggleMenu}
              aria-label="Close"
              style={{
                width: 30, height: 30, borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: chipBg, border: `1px solid ${rowBorder}`,
                color: textSub, cursor: 'pointer',
                minHeight: 'unset', minWidth: 'unset',
              }}
            >
              <X size={13} />
            </button>
          </div>

          {/* Contact rows */}
          <div style={{ padding: '10px' }}>
            {contactLinks.map((link, i) => (
              <a
                key={i}
                href={link.href}
                target={link.href.startsWith('http') ? '_blank' : undefined}
                rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'center',
                  gap: 10, padding: '10px 10px',
                  borderRadius: 12, marginBottom: 4,
                  textDecoration: 'none',
                  border: `1px solid ${rowBorder}`,
                  background: 'transparent',
                  transition: 'all 0.15s',
                  minHeight: 'unset', minWidth: 'unset',
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  background: `${link.color}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: link.color,
                }}>
                  {link.icon}
                </div>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: textMain }}>{link.label}</div>
                  <div style={{ fontSize: 11, color: textSub }}>{link.sub}</div>
                </div>
                <ArrowUpRight size={13} style={{ color: textSub, opacity: 0.6 }} />
              </a>
            ))}

            {/* WeChat */}
            <button
              onClick={handleWeChatClick}
              style={{
                display: 'flex', alignItems: 'center',
                gap: 10, padding: '10px 10px', width: '100%',
                borderRadius: 12,
                textDecoration: 'none',
                border: `1px solid ${rowBorder}`,
                background: 'transparent',
                transition: 'all 0.15s',
                cursor: 'pointer',
                position: 'relative',
                minHeight: 'unset', minWidth: 'unset',
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                background: 'rgba(34,197,94,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#16a34a',
              }}>
                <QrCode size={18} />
              </div>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: textMain }}>WeChat</div>
                <div style={{ fontSize: 11, color: textSub }}>18013289913</div>
              </div>
              <ChevronDown size={13} style={{
                color: textSub,
                transform: showWeChatQR ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }} />

              {showWeChatQR && (
                <div style={{
                  position: 'absolute', right: 'calc(100% + 10px)', top: '50%',
                  transform: 'translateY(-50%)',
                  padding: 12, borderRadius: 14,
                  background: panelBg, border: `1px solid ${panelBorder}`,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                  backdropFilter: 'blur(10px)',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: 11, color: textSub, marginBottom: 8 }}>Scan to add on WeChat</div>
                  <div style={{ background: '#fff', padding: 6, borderRadius: 8 }}>
                    <QRCodeCanvas value="https://wechat.com/your-account" size={100} level="H" includeMargin />
                  </div>
                </div>
              )}
            </button>
          </div>

          {/* Footer CTA */}
          <div style={{ padding: '10px 10px 14px' }}>
            <Link
              to="/contact"
              onClick={() => setIsOpen(false)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '10px', borderRadius: 12,
                background: 'linear-gradient(135deg, #dc2626, #991b1b)',
                color: '#fff', fontWeight: 700, fontSize: 13,
                textDecoration: 'none',
                minHeight: 'unset', minWidth: 'unset',
              }}
            >
              Send a Message
            </Link>
          </div>
        </div>
      )}

      {/* Collapsed button */}
      {!isOpen && (
        <button
          type="button"
          onClick={toggleMenu}
          aria-label="Open contact menu"
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center',
            padding: '16px 10px', borderRadius: 16,
            border: isDark ? '1px solid rgba(220,38,38,0.2)' : '1px solid rgba(220,38,38,0.15)',
            background: isDark ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.95)',
            boxShadow: isDark ? '0 8px 30px rgba(0,0,0,0.4)' : '0 8px 30px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            transition: 'all 0.3s',
            backdropFilter: 'blur(10px)',
            minHeight: 'unset', minWidth: 'unset',
          }}
        >
          {'CONTACT\nUS'.split('\n').map((line, li) => (
            <div key={li} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {li > 0 && <div style={{ height: 6 }} />}
              {line.split('').map((char, ci) => (
                <span key={ci} style={{
                  fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                  color: '#dc2626', lineHeight: 1.8,
                }}>{char}</span>
              ))}
            </div>
          ))}
        </button>
      )}

      {/* Scroll top */}
      {showScrollTop && (
        <button
          type="button"
          onClick={scrollToTop}
          aria-label="Scroll to top"
          style={{
            width: 38, height: 38, borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: isDark ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.95)',
            border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
            color: isDark ? '#6b7280' : '#9ca3af',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            cursor: 'pointer',
            transition: 'all 0.3s',
            minHeight: 'unset', minWidth: 'unset',
          }}
        >
          <ArrowUp size={15} />
        </button>
      )}

      <style>{`
        @keyframes sideIn {
          from { opacity: 0; transform: translateX(10px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default SideButton;